import config from '../../config/config';
import { siteUrl } from './seoMeta';

/**
 * Sitewide schema.org builders.
 *
 * Product schema stays in src/features/shop/utils/jsonLd.ts because it depends
 * on shop types; everything that describes the company or the site itself lives
 * here and is emitted from Layout.
 */

/** Stable @id so other nodes (Service.provider, Article.publisher) can reference this one. */
export const organizationId = `${siteUrl}/#organization`;
export const websiteId = `${siteUrl}/#website`;

const socialProfiles = [
  config.contact.facebook,
  config.contact.instagram,
  config.contact.linkedin,
].filter(Boolean);

/**
 * LocalBusiness for the Tangier premises.
 *
 * This is the highest-value schema on the site: it is what lets Google connect
 * the domain to the physical business, and it feeds the knowledge panel and
 * local pack. Everything here comes from src/config/config.ts so there is one
 * source of truth for NAP (name/address/phone) consistency — Google cross-checks
 * that against the Business Profile and directory listings.
 */
export const buildLocalBusinessJsonLd = (): Record<string, unknown> => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': organizationId,
  name: 'A2S SARL',
  legalName: 'Africa Supply Service SARL',
  alternateName: 'Africa Supply Service',
  url: siteUrl,
  logo: `${siteUrl}/logo.svg`,
  image: `${siteUrl}/images/og-default.jpg`,
  description:
    'A2S SARL (Africa Supply Service) — maintenance industrielle, traitement des eaux, ' +
    'compresseurs d’air, équipements de piscine, rénovation et fournitures industrielles à Tanger, Maroc.',
  telephone: config.contact.phone,
  email: config.contact.email,
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'A-11, ZAE Fahs Anjra',
    addressLocality: 'Tanger',
    addressRegion: 'Tanger-Tétouan-Al Hoceïma',
    addressCountry: 'MA',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: config.map.lat,
    longitude: config.map.lng,
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '08:00',
      closes: '18:00',
    },
  ],
  areaServed: { '@type': 'Country', name: 'Maroc' },
  currenciesAccepted: 'MAD',
  sameAs: socialProfiles,
});

/**
 * WebSite + SearchAction. The catalog already reads `?q=` (see ShopCatalogPage),
 * so the search target is real rather than aspirational.
 */
export const buildWebSiteJsonLd = (): Record<string, unknown> => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': websiteId,
  url: siteUrl,
  name: 'A2S SARL',
  inLanguage: 'fr',
  publisher: { '@id': organizationId },
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${siteUrl}/boutique?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
});

export interface BreadcrumbItem {
  name: string;
  /** Site-relative path, e.g. /boutique */
  path: string;
}

export const buildBreadcrumbJsonLd = (
  items: BreadcrumbItem[]
): Record<string, unknown> => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: `${siteUrl}${item.path}`,
  })),
});

export interface ArticleJsonLdInput {
  title: string;
  description: string;
  canonical: string;
  image?: string;
  datePublished?: string;
  dateModified?: string;
}

export const buildArticleJsonLd = ({
  title,
  description,
  canonical,
  image,
  datePublished,
  dateModified,
}: ArticleJsonLdInput): Record<string, unknown> => {
  const jsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: title,
    description,
    url: canonical,
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonical },
    inLanguage: 'fr',
    author: { '@id': organizationId },
    publisher: { '@id': organizationId },
  };

  if (image) jsonLd.image = image.startsWith('http') ? image : `${siteUrl}${image}`;
  if (datePublished) jsonLd.datePublished = datePublished;
  jsonLd.dateModified = dateModified || datePublished || undefined;
  if (!jsonLd.dateModified) delete jsonLd.dateModified;

  return jsonLd;
};

export interface FaqEntry {
  question: string;
  answer: string;
}

/**
 * FAQPage from the Q&A already rendered by BlogFAQ. Google requires the answers
 * to be visible on the page — they are, so this is a legitimate rich result
 * rather than hidden markup.
 */
export const buildFaqJsonLd = (entries: FaqEntry[]): Record<string, unknown> => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: entries.map((entry) => ({
    '@type': 'Question',
    name: entry.question,
    acceptedAnswer: { '@type': 'Answer', text: entry.answer },
  })),
});

export interface ServiceJsonLdInput {
  name: string;
  description: string;
  canonical: string;
  image?: string;
}

export const buildServiceJsonLd = ({
  name,
  description,
  canonical,
  image,
}: ServiceJsonLdInput): Record<string, unknown> => {
  const jsonLd: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    description,
    url: canonical,
    serviceType: name,
    provider: { '@id': organizationId },
    areaServed: { '@type': 'Country', name: 'Maroc' },
  };

  if (image) jsonLd.image = image.startsWith('http') ? image : `${siteUrl}${image}`;

  return jsonLd;
};
